import { create } from "zustand";

/**
 * Compliance Slasher Enforcement Store (v2)
 * 
 * Manages compliance gap scoring, enforcement penalties, passport downgrades,
 * and remediation tracking.
 */
export interface SlashStoreState {
  gapScore: number; // 0-100
  severity: "low" | "medium" | "high";
  crossBorderFactor: number;
  supplyChainMultiplier: number;
  subsidyEligible: boolean;
  remediationDays: number;
  remediationCostEUR: number;

  // Actions
  setGapScore: (score: number) => void;
  setSeverity: (severity: "low" | "medium" | "high") => void;
  setCrossBorderFactor: (factor: number) => void;
  setSupplyChainMultiplier: (multiplier: number) => void;
  setSubsidyEligible: (eligible: boolean) => void;
  setRemediationDays: (days: number) => void;
  setRemediationCostEUR: (cost: number) => void;

  // Derived outputs
  getEnforcementPenalty: () => number;
  getPassportDowngrade: () => number;
  getCo2TaxAdjustment: () => number;
  getFrictionIndex: () => number;

  // Export
  exportEnforcementReport: () => void;
}

export const useSlashStore = create<SlashStoreState>((set, get) => {
  const severityWeight = (severity: "low" | "medium" | "high") => {
    switch (severity) {
      case "low": return 0.8;
      case "medium": return 1.0;
      case "high": return 1.2;
    }
  };

  return {
    gapScore: 0,
    severity: "low",
    crossBorderFactor: 1.0,
    supplyChainMultiplier: 1.0,
    subsidyEligible: true,
    remediationDays: 0,
    remediationCostEUR: 0,

    setGapScore: (score) => set({ gapScore: Math.max(0, Math.min(100, score)) }),
    setSeverity: (severity) => set({ severity }),
    setCrossBorderFactor: (factor) => set({ crossBorderFactor: factor }),
    setSupplyChainMultiplier: (multiplier) => set({ supplyChainMultiplier: multiplier }),
    setSubsidyEligible: (eligible) => set({ subsidyEligible: eligible }),
    setRemediationDays: (days) => set({ remediationDays: days }),
    setRemediationCostEUR: (cost) => set({ remediationCostEUR: cost }),

    getEnforcementPenalty: () => {
      const state = get();
      const weight = severityWeight(state.severity);
      return state.gapScore * weight * state.supplyChainMultiplier;
    },

    getPassportDowngrade: () => {
      return Math.floor(get().getEnforcementPenalty() / 20);
    },

    getCo2TaxAdjustment: () => {
      return get().getEnforcementPenalty() * 0.5;
    },

    getFrictionIndex: () => {
      const state = get();
      return state.crossBorderFactor * 0.3 + state.supplyChainMultiplier * 0.2;
    },

    exportEnforcementReport: () => {
      const state = get();
      const report = {
        gapScore: state.gapScore,
        severity: state.severity,
        enforcementPenalty: state.getEnforcementPenalty(),
        passportDowngrade: state.getPassportDowngrade(),
        co2TaxAdjustment: state.getCo2TaxAdjustment(),
        frictionIndex: state.getFrictionIndex(),
        subsidyEligible: state.subsidyEligible,
        remediationDays: state.remediationDays,
        remediationCostEUR: state.remediationCostEUR,
      };

      const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "enforcementReport.json";
      a.click();
      URL.revokeObjectURL(url);
    },
  };
});











